import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface SortRow { id: string; name: string; price: string | number; image_url?: string | null; is_featured?: boolean; is_available?: boolean; categoryLabel: string }

/**
 * Drag-and-drop ordering list (native HTML5 DnD, no dependency). Rows are the
 * currently visible (filtered) items; `onMove` receives the new order of those
 * same ids. ▲▼ and "to top" buttons cover small moves and keyboard users.
 */
export function SortableMenuList({ rows, onMove, disabled }: { rows: SortRow[]; onMove: (ids: string[]) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const ids = rows.map((r) => r.id);

  const move = (id: string, to: number) => {
    const from = ids.indexOf(id);
    if (from < 0 || to < 0 || to >= ids.length || from === to) return;
    const next = ids.slice();
    next.splice(from, 1);
    next.splice(to, 0, id);
    onMove(next);
  };

  const btn = { border: "1px solid #E5E7EB", background: "#fff", borderRadius: 6, width: 30, height: 30, cursor: "pointer", fontSize: 13 } as const;

  return (
    <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, background: "#fff", overflow: "hidden" }}>
      {rows.length === 0 && <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF" }}>{t("menu.sortEmpty")}</div>}
      {rows.map((r, i) => (
        <div
          key={r.id}
          draggable={!disabled}
          onDragStart={(e) => { setDragId(r.id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", r.id); }}
          onDragOver={(e) => { if (dragId) { e.preventDefault(); if (overId !== r.id) setOverId(r.id); } }}
          onDrop={(e) => { e.preventDefault(); if (dragId && dragId !== r.id) move(dragId, ids.indexOf(r.id)); setDragId(null); setOverId(null); }}
          onDragEnd={() => { setDragId(null); setOverId(null); }}
          style={{
            display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
            borderBottom: "1px solid #F1F2F4", opacity: dragId === r.id ? 0.4 : 1, cursor: disabled ? "default" : "grab",
            boxShadow: overId === r.id && dragId && dragId !== r.id ? "inset 0 3px 0 #C2410C" : "none", background: "#fff",
          }}
        >
          <span aria-hidden style={{ color: "#9CA3AF", fontSize: 18, userSelect: "none", width: 18 }}>⠿</span>
          <span style={{ width: 28, color: "#9CA3AF", fontSize: 12, textAlign: "right" }}>{i + 1}</span>
          {r.image_url
            ? <img src={r.image_url} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }} />
            : <div style={{ width: 40, height: 40, borderRadius: 8, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#6B7280" }}>{r.name.slice(0, 1).toUpperCase()}</div>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.name}{r.is_featured ? <span style={{ marginLeft: 8, fontSize: 11, color: "#C2410C" }}>{t("menu.sortPinned")}</span> : null}
              {r.is_available === false ? <span style={{ marginLeft: 8, fontSize: 11, color: "#9CA3AF" }}>{t("menu.sortUnavailable")}</span> : null}
            </div>
            <div style={{ fontSize: 12, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.categoryLabel}</div>
          </div>
          <div style={{ fontVariantNumeric: "tabular-nums", color: "#374151", width: 80, textAlign: "right" }}>RM {Number(r.price).toFixed(2)}</div>
          <div style={{ display: "flex", gap: 4 }}>
            <button type="button" style={btn} disabled={disabled || i === 0} title={t("menu.sortTop")} onClick={() => move(r.id, 0)}>⤒</button>
            <button type="button" style={btn} disabled={disabled || i === 0} title={t("menu.sortUp")} onClick={() => move(r.id, i - 1)}>▲</button>
            <button type="button" style={btn} disabled={disabled || i === rows.length - 1} title={t("menu.sortDown")} onClick={() => move(r.id, i + 1)}>▼</button>
          </div>
        </div>
      ))}
    </div>
  );
}
